import { GUID } from "@faro-lotv/foundation";
import { MarkupIElement, URI } from "@faro-lotv/ielement-types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export enum AiReviewViewMode {
  stepByStep = "stepByStep",
  listView = "listView",
}

export enum AiAssetReviewStatus {
  pending = "pending",
  approved = "approved",
  rejected = "rejected",
}

/** Represents an AI-generated annotation that can be reviewed and approved/rejected. */
export interface AiGeneratedAnnotation {
  /** Unique identifier for the annotation (GUID) */
  id: GUID;
  /** The underlying markup element containing the annotation data */
  markup: MarkupIElement;
  /** Display name/title of the annotation */
  title: string;
  /** AI-detected asset type as a plain string */
  tag: string;
  /** Name of the area/section name that this annotation is located in */
  area?: string;
  /** Thumbnail image URI for the annotation */
  thumbnail?: URI | null;
  /** Current review status (pending, approved, or rejected) */
  status: AiAssetReviewStatus;
  /** Review notes added by the reviewer, used by AI team for feedback */
  reviewNotes?: string;
}

export interface AiAssetReviewState {
  viewMode: AiReviewViewMode;
  currentAnnotationId?: GUID;
  isActive: boolean;
}

const initialState: AiAssetReviewState = {
  viewMode: AiReviewViewMode.stepByStep,
  currentAnnotationId: undefined,
  isActive: false,
};

const aiAssetReviewSlice = createSlice({
  name: "aiAssetReview",
  initialState,
  reducers: {
    /**
     * Set the view mode of the AI Asset Review tool.
     *
     * @param state The current state
     * @param action The new view mode to set
     */
    setViewMode: (state, action: PayloadAction<AiReviewViewMode>) => {
      state.viewMode = action.payload;
    },

    /**
     * Set or clear the current annotation under review by its id
     *
     * @param state The current state
     * @param action The new current annotation id, or undefined to clear
     */
    setCurrentAnnotationId: (
      state,
      action: PayloadAction<GUID | undefined>,
    ) => {
      state.currentAnnotationId = action.payload;
    },

    // Status is derived from labels; mutation is handled via thunks

    /**
     * Set whether the AI Asset Review tool is active.
     *
     * @param state The current state
     * @param action The active flag to set
     */
    setIsActive: (state, action: PayloadAction<boolean>) => {
      state.isActive = action.payload;
    },
  },
});

export const { setViewMode, setCurrentAnnotationId, setIsActive } =
  aiAssetReviewSlice.actions;

export const aiAssetReviewReducer = aiAssetReviewSlice.reducer;
